import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { jwtDecode } from 'jwt-decode';
import { User } from 'oidc-client-ts';
import { refreshToken } from '../api/tokenValidation';
import { getAuthority } from './auth';
import { getUserManager } from './userManager';

interface TokenData {
  access_token: string;
  refresh_token?: string;
}

interface CustomJwtPayload {
  sub?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  organization?: any[];
  role?: string;
  exp?: number;
  scope?: string[];
}

interface ApiClientOptions {
  baseURL: string;
  enableBlobUrlPreview?: boolean;
}

const { VITE_COMPANION_CLIENT_ID: COMPANION_CLIENT_ID } = import.meta.env;

// Shared state for token refresh across all instances
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export const createApiClient = (options: ApiClientOptions): AxiosInstance => {
  const { baseURL, enableBlobUrlPreview = false } = options;

  const apiClient = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
  });

  // Request interceptor - add auth token
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const LSKey = `oidc.user:${getAuthority()}:${
        COMPANION_CLIENT_ID as string
      }`;
      const tokenData = localStorage.getItem(LSKey) || '';
      config.headers = config.headers || {};
      if (tokenData) {
        config.headers.Authorization = `Bearer ${
          (JSON.parse(tokenData) as TokenData).access_token
        }`;
      }

      // Add preview_mode parameter if Blob Url is enabled (only for baseApi)
      if (enableBlobUrlPreview) {
        const blobUrlEnabled =
          sessionStorage.getItem('blobUrlEnabled') === 'true';
        if (
          blobUrlEnabled &&
          config.url &&
          !config.url.includes('/api/genres')
        ) {
          const separator = config.url.includes('?') ? '&' : '?';
          config.url = `${config.url}${separator}preview_mode=true`;
        }
      }

      return config;
    },
    (error: AxiosError) => Promise.reject(error),
  );

  // Response interceptor - handle 401 errors with token refresh
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Check if error is 401 and we haven't retried yet
      if (error.response?.status === 401 && !originalRequest._retry) {
        const LSKey = `oidc.user:${getAuthority()}:${
          COMPANION_CLIENT_ID as string
        }`;
        const tokenDataStr = localStorage.getItem(LSKey);

        if (tokenDataStr) {
          try {
            const tokenData = JSON.parse(tokenDataStr) as TokenData;

            // Only attempt refresh if user was authenticated (has refresh_token)
            if (tokenData.refresh_token) {
              if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                  failedQueue.push({ resolve, reject });
                })
                  .then(() => {
                    return apiClient(originalRequest);
                  })
                  .catch((err) => {
                    return Promise.reject(err);
                  });
              }

              originalRequest._retry = true;
              isRefreshing = true;

              try {
                const {
                  access_token,
                  id_token,
                  refresh_token: newRefreshToken,
                } = await refreshToken(tokenData.refresh_token);

                if (access_token) {
                  const userData = jwtDecode<CustomJwtPayload>(access_token);

                  const oidcUser = {
                    access_token: access_token,
                    id_token: id_token || undefined,
                    refresh_token: newRefreshToken,
                    token_type: 'Bearer',
                    profile: {
                      sub: userData.sub || '',
                      email: userData.email || '',
                      name: userData.name || '',
                      firstName: userData.firstName || '',
                      lastName: userData.lastName || '',
                      organization: userData.organization || [],
                      role: userData.role,
                      iss: '',
                      aud: '',
                      exp: userData.exp || Math.floor(Date.now() / 1000) + 3600,
                      iat: Math.floor(Date.now() / 1000),
                    },
                    expires_at: userData.exp,
                    scope:
                      userData?.scope?.join(' ') ||
                      'openid profile offline_access napsterai.companions.identity.main',
                    session_state: null,
                    state: null,
                    expires_in: userData.exp
                      ? userData.exp - Math.floor(Date.now() / 1000)
                      : 3600,
                    expired: false,
                    scopes:
                      userData.scope ||
                      'openid profile offline_access napsterai.companions.identity.main',
                    toStorageString: function () {
                      return JSON.stringify(this);
                    },
                  };

                  const userManager = getUserManager();
                  await userManager.storeUser(oidcUser as unknown as User);

                  userManager.events.load(oidcUser as unknown as User);

                  // Update the authorization header for the retry
                  if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${access_token}`;
                  }

                  processQueue(null, access_token);
                  isRefreshing = false;

                  // Retry the original request
                  return apiClient(originalRequest);
                }
              } catch (refreshError) {
                processQueue(
                  refreshError instanceof Error
                    ? refreshError
                    : new Error('Token refresh failed'),
                  null,
                );
                isRefreshing = false;
                return Promise.reject(refreshError);
              }
            }
          } catch (parseError) {
            console.error('Error parsing token data:', parseError);
          }
        }
      }

      // Log the error
      if (error.response) {
        console.error(
          'Request error:',
          error.response.status,
          error.response.data,
        );
      } else if (error.request) {
        console.error('No response from server:', error.request);
      } else {
        console.error('Request configuration error:', error.message);
      }
      return Promise.reject(error);
    },
  );

  return apiClient;
};
